/**
 * Glitchtip wiring for the SvelteKit client. Empty DSN disables init silently
 * (e.g. local dev without observability).
 *
 * Sentry's GlobalHandlers integration is explicitly DISABLED — every uncaught
 * error reaches Glitchtip via the window.error / unhandledrejection listeners
 * in errors.svelte.ts, which funnel through `reportError`. That keeps the
 * toast and Glitchtip paths in lockstep: one event per failure, identical
 * msg / tags / cause-chain. Use `reportError` / `reportSynthError` from
 * $lib/stores/errors.svelte for all in-code reporting.
 */

import * as Sentry from '@sentry/sveltekit';
import type { HandleClientError } from '@sveltejs/kit';
import {
  PUBLIC_GLITCHTIP_DSN,
  PUBLIC_WAND_ENV,
  PUBLIC_APP_VERSION,
} from '$env/static/public';
import { reportError } from '$lib/stores/errors.svelte';

// Belt-and-suspenders scrubbing for breadcrumbs that might leak OIDC code,
// Bearer tokens, or password-like fields. Wrapper logger gates debug/info, but
// any third-party SDK or future call could still produce a sensitive crumb.
const SENSITIVE_RX = /\b(bearer\s+\S+|code=[\w-]{8,}|password=\S+|id_token_hint=\S+|access_token=\S+)\b/i;

if (PUBLIC_GLITCHTIP_DSN) {
  try {
    Sentry.init({
      dsn: PUBLIC_GLITCHTIP_DSN,
      tunnel: '/api/sentry-tunnel',
      environment: PUBLIC_WAND_ENV || 'dev',
      release: PUBLIC_APP_VERSION || 'dev',
      tracesSampleRate: 0,
      sendDefaultPii: false,
      maxBreadcrumbs: 200,
      beforeBreadcrumb(crumb) {
        const haystack = `${crumb.message ?? ''} ${JSON.stringify(crumb.data ?? '')}`;
        if (SENSITIVE_RX.test(haystack)) return null;
        return crumb;
      },
      // Drop GlobalHandlers — our own window.error / unhandledrejection
      // listeners in errors.svelte.ts route every uncaught error through
      // reportError, which ships to Sentry. Letting Sentry's GlobalHandlers
      // also fire would duplicate events.
      // Drop BrowserSession — we don't track release-adoption sessions.
      // Replace Breadcrumbs with a copy that skips history (the default patches
      // history.pushState/replaceState and triggers SvelteKit's
      // "use $app/navigation" warning); navigation context comes from
      // afterNavigate in +layout.svelte.
      integrations: (defaults) => [
        ...defaults.filter(
          (i) => i.name !== 'Breadcrumbs' && i.name !== 'BrowserSession' && i.name !== 'GlobalHandlers',
        ),
        Sentry.breadcrumbsIntegration({ history: false }),
      ],
    });
  } catch (cause) {
    reportError('Glitchtip initialization failed', cause, { source: 'sentry.init' });
  }
}

// Tag transport-layer fetch errors with the URL so the toast isn't
// a bare "Failed to fetch". After Sentry.init so its fetch breadcrumbs
// run inside ours.
{
  const origFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    try {
      return await origFetch(input, init);
    } catch (err) {
      const url = typeof input === 'string'
        ? input
        : input instanceof URL ? input.toString() : input.url;
      const msg = err instanceof Error ? err.message : String(err);
      throw new Error(`fetch "${url}": ${msg}`, { cause: err });
    }
  };
}

export const handleError: HandleClientError = ({ error }) => {
  reportError('sveltekit client error', error, { source: 'sveltekit.handleError' });
};


// Test surface: in dev builds (vite serve, not vite build) expose a promise
// that resolves to the wand RPC module + enum helpers. Playwright tests
// drive setup operations (seed a runtime instance, fork a layer, ...)
// through this so e2e specs don't have to navigate the UI for every
// prerequisite. Production builds dead-code-eliminate the whole branch.
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  type DevTestApi = typeof import('$lib/api/wand') & {
    instancePoolFromStr: typeof import('$lib/utils/enums').instancePoolFromStr;
    types: typeof import('@helinks/wand-api-thrift/wand_types');
  };
  const testApiReady: Promise<DevTestApi> = (async () => {
    const [wand, enums, types] = await Promise.all([
      import('$lib/api/wand'),
      import('$lib/utils/enums'),
      import('@helinks/wand-api-thrift/wand_types'),
    ]);
    return Object.assign({} as DevTestApi, wand, {
      instancePoolFromStr: enums.instancePoolFromStr,
      types,
    });
  })();
  (window as unknown as { __testApiReady?: Promise<DevTestApi> }).__testApiReady = testApiReady;
}